import { LoadingOutlined } from "@ant-design/icons";
import {} from "antd";
import { Input, notification, Select, Spin } from "antd";
import SVG from "components/common/SVG";
import { useCallback, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { connect } from "react-redux";

import { createOrganizationWorkspace, getAllOrganizations } from "../../../../store/organization/duck";
import { getUploadPicture } from "../../../../store/videogrid/duck";
import CustomModal from "../CustomModal";
import styles from "./style.module.scss";

const antIcon = <LoadingOutlined style={{ fontSize: 24, align: "center" }} spin />;

const mapDispatchToProps = {
  getUploadPicture,
  getAllOrganizations,
  createOrganizationWorkspace,
};

const AddWorkspaceModal = ({
  getUploadPicture,
  getAllOrganizations,
  createOrganizationWorkspace,
  showModal,
  setShowModal,
  closeAndFetchModal,
}) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [organizationOptions, setOrganizationOptions] = useState(
    showModal?.organizationId ? [{ label: showModal?.displayName, value: showModal?.organizationId }] : [],
  );
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(showModal?.organizationId || null);

  const handleClose = () => {
    setShowModal(null);
    closeAndFetchModal?.();
    setName("");
    setSelectedOrganizationId(null);
    setUploadedImageUrl("");
  };

  const handleAddWorkspace = async () => {
    try {
      setLoading(true);
      const payload = {
        name,
        logo: uploadedImageUrl,
        organizationId: selectedOrganizationId,
      };

      const response = await createOrganizationWorkspace(payload);
      if (response?.value?.status) {
        notification.success({ message: response?.value?.message });
      }

      handleClose();
    } catch (error) {
      console.log(error);
      if (error.message) {
        notification.error({ message: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadFileAndGetUrl = async (data, type) => {
    setLoading(true);
    if (type !== "image") {
      notification["warning"]({ message: "Wrong file uploaded!" });
    } else {
      const { value } = await getUploadPicture(data);
      if (value.status) {
        setUploadedImageUrl(value.data);
      }
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    const files = acceptedFiles;
    const file = files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = async () => {
          const data = new FormData();
          data.append("file", file);
          data.append("filename", file.name);
          let type = file?.type.split("/")[0];
          let title = file.name;
          await uploadFileAndGetUrl(data, type, title);
        };
      };
      reader.readAsDataURL(file);
    } else {
      notification["warning"]({ message: "Please select valid file!" });
    }
  };

  const fetchAllOrganizations = useCallback(async () => {
    try {
      const response = await getAllOrganizations();

      if (response?.value?.organizations) {
        setOrganizationOptions(
          response.value.organizations.map(({ organizationId, displayName }) => ({
            value: organizationId,
            label: displayName,
          })),
        );

        if (showModal?.organizationId) {
          setSelectedOrganizationId(showModal.organizationId);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [getAllOrganizations, showModal?.organizationId]);

  useEffect(() => {
    if (!showModal?.organizationId) {
      fetchAllOrganizations();
    }
  }, [fetchAllOrganizations, showModal?.organizationId]);

  return (
    <>
      <CustomModal
        showModal={typeof showModal === "boolean" ? showModal : !!showModal?.organizationId}
        setShowModal={() => setShowModal(null)}
        contentLabel="Add Workspace"
        primaryCtaText="Add"
        secondaryCtaText="Close"
        primaryCtaHandler={handleAddWorkspace}
        secondaryCtaHandler={handleClose}
        primaryCtaEnabled={name && uploadedImageUrl && selectedOrganizationId}
      >
        <div className={styles.formContainer}>
          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Organization</div>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              className="custom-ant-select-v2"
              disabled={!!showModal?.organizationId}
              value={selectedOrganizationId}
              onChange={(value) => {
                setSelectedOrganizationId(value);
              }}
              options={organizationOptions}
            />
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Workspace name</div>
            <Input
              className={styles.textInput}
              disabled={!selectedOrganizationId}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Workspace logo</div>

            <Dropzone accept="image/*" multiple={false} onDrop={onDrop}>
              {({ getRootProps, getInputProps }) => (
                <div className={styles.dropzone}>
                  <div className={styles.dropzoneContainer} {...getRootProps()}>
                    <input {...getInputProps()} />

                    {uploadedImageUrl ? (
                      <img src={uploadedImageUrl} className={styles.previewImage} alt="preview" />
                    ) : (
                      <>
                        <SVG iconName={"images/upload.svg"} className={styles.dropzoneIcon} />
                        {loading ? <Spin indicator={antIcon}></Spin> : ""}

                        <div className={styles.browseCta}>Browse</div>
                        <div className={styles.dropdownText}>or, drag & drop your file here</div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </Dropzone>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default connect((/*state*/) => ({}), mapDispatchToProps)(AddWorkspaceModal);
